import { z } from "zod";

/**
 * Extra profile fields a PUBLIC-community card surfaces, stored as a single
 * `users.profile_metadata` JSONB blob. Keeping the knobs in one client-queried
 * column (instead of column-per-field sprawl) keeps the Zero schema small and
 * additive — mirrors `workspaces.community_settings`.
 *
 * These are GLOBAL profile fields (a user has one identity), but they only
 * render on the public profile card. All optional; absence renders nothing.
 */

/** Social platforms we render a labelled link/icon for. `other` = generic link. */
export const SOCIAL_PLATFORMS = [
  "github",
  "x",
  "linkedin",
  "mastodon",
  "youtube",
  "bluesky",
  "discord",
  "website",
  "other",
] as const;
export type SocialPlatform = (typeof SOCIAL_PLATFORMS)[number];

/** True only for an `https:` URL. Rejects javascript:/data:/vbscript:/http:. */
export function isHttpsUrl(value: string): boolean {
  try {
    return new URL(value).protocol === "https:";
  } catch {
    return false;
  }
}

/** True for an `http:`/`https:` URL — a safe-to-render web link (no js:/data:). */
export function isWebLinkUrl(value: string): boolean {
  try {
    const p = new URL(value).protocol;
    return p === "http:" || p === "https:";
  } catch {
    return false;
  }
}

export const socialLinkSchema = z.object({
  platform: z.enum(SOCIAL_PLATFORMS),
  // https only — authoritative (runs on the server mutator too). Zod's `.url()`
  // alone ACCEPTS javascript:/data:, so the refine is the real guard against a
  // stored-XSS href on the public profile card.
  url: z.string().max(500).refine(isHttpsUrl, "Links must be https:// URLs"),
});
export type SocialLink = z.infer<typeof socialLinkSchema>;

export const profileMetadataSchema = z.object({
  /** Up to 8 social/profile links shown as an icon row on the public card. */
  social_links: z.array(socialLinkSchema).max(8).optional(),
  /** Free-text "what I'm building right now" status. */
  building_now: z.string().max(280).optional(),
  /**
   * Linked GitHub username (NOT a URL — the server extracts the handle to
   * defeat SSRF and fetches public data live + cached). GitHub usernames are
   * ≤39 chars, alphanumeric or single hyphens.
   */
  github_username: z
    .string()
    .max(39)
    .regex(/^[a-z\d](?:[a-z\d]|-(?=[a-z\d])){0,38}$/i)
    .optional(),
});

export type ProfileMetadata = z.infer<typeof profileMetadataSchema>;

/** Null-safe accessor — treat a missing/empty blob as `{}`. */
export function readProfileMetadata(
  raw: ProfileMetadata | null | undefined,
): ProfileMetadata {
  return raw ?? {};
}
